import { IReactionDisposer, observable, reaction, transaction } from 'mobx';
import { triggerFontDownload } from '@utils/fonts';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { isVipFont, isCustomFont, setFontNameAlias } from '@utils/fonts/font-list-loader';
import { IFontManager, ModelKey, FontDownloadCallback, IFontManagerInjectable, IFontsUsingStatus } from './font-manager.interface';
import { camelize } from 'utils';
import { UncommonFontLogic } from '@src/slide/content-viewer/service/font-manager/core/uncommon-font-logic';
import { SlideDataServices, onSlideAppReady } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { isGroupModel, isShapeModel, isTableModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';

/**
 * 云字体管理器
 */
export default class FontManager implements IFontManager {
    /**
     * slide-id 对此页面正在使用的云字体 fontFamily 的映射
     */
    public readonly usingFontFamiliesBySlide: Map<string, Set<string>>;

    /**
     * 已加载完成的所有 fontFamilies
     */
    @observable public readonly downloadedFontFamilies: string[];

    /**
     * @description 记录所有页面正在使用的字体属性。业务层需要负责及时清除
     * @type Set<string>
     * @access public
     */
    @observable public readonly globalUsingFontsCache: Set<string> = new Set();

    /**
     * 记录每一个 shape 在字体下载完成时需要被执行的回调映射关系
     * key 是 editor 对应的 shapeModel 的 key，value 是该 shape 所对应的所有回调
     * 一个 shape 有可能对应多个 editor （编辑区，左侧预览区等等），所以映射关系是 key 到一个回调数组
     */
    public readonly editorOnFontDownloadedCallbacks: Map<ModelKey, FontDownloadCallback[]> = new Map();

    /**
     * 记录每一个 shape 正在使用的字体集合
     * key 是 editor 对应的 shapeModel 的key，value 是该 editor 正在使用的字体集合
     */
    public readonly shapeModelUsingFonts: Map<ModelKey, Set<string>> = new Map();

    public uncommonFontLogic?: UncommonFontLogic;

    private disposeFontDownloadedReaction: IReactionDisposer | undefined;

    private disposeGlobalUsingFontsChangeReaction: IReactionDisposer | undefined;

    public constructor(private uiDataService: IUiDataService, private injectable?: IFontManagerInjectable) {
        this.usingFontFamiliesBySlide = new Map();
        this.downloadedFontFamilies = [];
        if (!injectable?.disableUncommonFontLogic) {
            this.uncommonFontLogic = new UncommonFontLogic();
        }
        // 是否禁用云字体
        if (injectable?.disableCloudFont) return;
        if (!injectable?.disableVip) {
            this.initVipStatus();
        }
        this.registerFontDownloadedReaction();
    }

    /**
     * 注册传入 slideId 正在使用的云字体，并根据判断触发下载
     * @param fontFamily 正在使用的字体
     */
    public async triggerFontDownload(fontFamily: string): Promise<void> {
        const fallbackFontFamily = this.fallbackToFontNameAlias(fontFamily);
        const hasFontDownloaded = await triggerFontDownload(fallbackFontFamily);
        if (fallbackFontFamily !== fontFamily) {
            // is using alias
            await setFontNameAlias(fallbackFontFamily, fontFamily);
        }
        transaction(() => {
            if (hasFontDownloaded) {
                this.downloadedFontFamilies.push(fallbackFontFamily);
            }
            if (fallbackFontFamily !== fontFamily) {
                this.downloadedFontFamilies.push(fontFamily);
            }
        });
    }

    /**
     * 数据遍历至 shapeModel 时所调用的云字体特殊逻辑，需要收集当前 shapeModel 都使用了哪些字体
     * @param shapeModel 待收集字体信息的 shapeModel
     */
    public collectUsingFontsForShapeModel(shapeModel: IShapeModel): void {
        if (!shapeModel) {
            return;
        }
        // 是否禁用云字体
        if (this.injectable?.disableCloudFont) return;
        const fonts: string[] = [];
        shapeModel.paragraphList?.forEach?.((paragraphListItem) => {
            paragraphListItem?.textRuns?.forEach?.((textRun) => {
                if (textRun.styles?.fonts) {
                    Object.values(textRun.styles.fonts).forEach((font) => {
                        if (typeof font === 'string') {
                            fonts.push(font);
                        }
                    });
                }
            });
        });
        if (shapeModel.isPlaceholder) {
            // 占位符特殊逻辑：如果母版中设置了主题字体为云字体，则占位符全部需要使用该云字体
            // 因此所有占位符需要监听该字体下载状态做重渲染
            const { presentationRef } = shapeModel;
            presentationRef && fonts.push(presentationRef.globalFont);
        }
        this.onRecordUsingFonts(shapeModel.key, fonts);
    }

    /**
     * 垃圾回收
     */
    public dispose(): void {
        this.disposeFontDownloadedReaction?.();
        this.disposeGlobalUsingFontsChangeReaction?.();
        this.disposeAllEditorFontDownloadCallbacks();
    }


    /**
     * 注册 editor 字体下载完成后的回调事件
     * @param shapeModelKey 待注册 callback 的 editor 对应的 shapeModel 的 key
     * @param callback 在字体下载完成后执行的回调
     */
    public registerOnFontDownloadCallback(shapeModelKey: string, callback: FontDownloadCallback): void {
        // 是否禁用云字体
        if (this.injectable?.disableCloudFont) return;
        if (!this.editorOnFontDownloadedCallbacks.has(shapeModelKey)) {
            this.editorOnFontDownloadedCallbacks.set(shapeModelKey, []);
        }
        const callbacks = this.editorOnFontDownloadedCallbacks.get(shapeModelKey);
        callbacks?.push(callback);
    }

    /**
     * 取消单一 editor 的字体下载完成回调注册
     * @param shapeModelKey editor 对应的 shapeModel 的 key
     * @param downloadedCallback editor 对应的回调实例
     */
    public disposeFontDownloadCallbackByShapeModelKey(
        shapeModelKey: string,
        downloadedCallback: FontDownloadCallback,
    ): void {
        if (this.editorOnFontDownloadedCallbacks.has(shapeModelKey)) {
            const callbacks = this.editorOnFontDownloadedCallbacks.get(shapeModelKey);
            callbacks?.some((callback, i) => {
                if (callback === downloadedCallback) {
                    callbacks.splice(i, 1);
                    return true;
                }
                return false;
            });
        }
    }

    public async collectFontsUsingStatus(): Promise<IFontsUsingStatus> {
        if (this.injectable?.injectedFontUsingStatus) {
            return this.injectable.injectedFontUsingStatus;
        }
        const res: IFontsUsingStatus = {
            hasCustomFonts: false,
            hasTencentSans: false,
            hasVipFonts: false,
        };
        const usingFonts: Set<string> = new Set<string>();
        this.uiDataService.presentation.children.forEach((slideModel) => {
            slideModel.children.forEach((model: IBaseModel) => {
                this.iterateOnModelChildren(model, usingFonts);
            });
        });
        const usingFontsArr = [...usingFonts].map(font => this.fallbackToFontNameAlias(font));
        const hasVipFontsPromises = usingFontsArr.map(font => isVipFont(font));
        const hasCustomFontsPromises = usingFontsArr.map(font => isCustomFont(font));
        try {
            res.hasVipFonts = (await Promise.all(hasVipFontsPromises)).reduce((acc, cur) => acc || cur, false);
            res.hasCustomFonts = res.hasVipFonts
                || (await Promise.all(hasCustomFontsPromises)).reduce((acc, cur) => acc || cur, false);
        } catch (e) {
            window.log?.error('云字体判断失败！', e);
            res.hasVipFonts = false;
            res.hasCustomFonts = false;
        }
        res.hasTencentSans = usingFontsArr.some(font => font === '腾讯体 W3' || font === '腾讯体 W7');
        return res;
    }

    private onRecordUsingFonts(shapeModelKey: ModelKey, fonts: string[]): void {
        if (!this.shapeModelUsingFonts.has(shapeModelKey)) {
            this.shapeModelUsingFonts.set(shapeModelKey, new Set<string>());
        }
        const usingFonts = this.shapeModelUsingFonts.get(shapeModelKey) as Set<string>;
        fonts.forEach((font) => {
            usingFonts.add(font);
            this.globalUsingFontsCache.add(font);
        });
    }

    private disposeAllEditorFontDownloadCallbacks(): void {
        this.editorOnFontDownloadedCallbacks.clear();
    }

    private initVipStatus(): void {
        this.initMyselfVipStatus();
        this.initOwnerVipStatus();
    }

    private async initMyselfVipStatus(): Promise<void> {
        const apiService = await OnlineEditServices.OnlineApiService.promise();
        const res = await apiService.getUserInfo();
        if (res?.data?.cgicode === 0 && res?.data?.retcode === 0) {
            const user = res.data;
            if (window.clientVars) {
                window.clientVars.vipInfo = camelize(user.vip_info);
            }
        }
    }

    /**
     * // TODO
     * 目前只有个性字体需求用到了文档的 owner vip 状态，所以这个请求放到 fontManager 里来做。以后如果有其他需求也需要用到这个状态，
     * 则应该考虑重构此方法至项目中其他位置
     */
    private initOwnerVipStatus(): void {
        onSlideAppReady(() => {
            const unparsedFileId = this.getPadId();
            const prepend = '300000000$';
            let fileId = unparsedFileId;
            if (typeof unparsedFileId === 'string' && unparsedFileId.startsWith(prepend)) {
                fileId = unparsedFileId.substring(prepend.length);
            }
            const args = {
                file_id: fileId,
            };
            fetch('/api/personalize/owner/viplevel', {
                method: 'POST',
                body: JSON.stringify(args),
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then(response => response.json())
                .then((responseJson) => {
                    if (responseJson.retcode === 0) {
                        window.clientVars.ownerVipInfo = {
                            vip_level: responseJson.result.vip_level,
                        };
                    }
                })
                .catch((e) => {
                    window.log.error(e);
                });
        });
    }

    private getPadId(): string {
        return SlideDataServices.DocInfoManager()?.padId ?? '';
    }

    private registerFontDownloadedReaction(): void {
        this.disposeFontDownloadedReaction = reaction(
            () => this.downloadedFontFamilies.length,
            () => {
                const latestDownloadedFont = this.downloadedFontFamilies[this.downloadedFontFamilies.length - 1];
                this.editorOnFontDownloadedCallbacks.forEach((callbacks, shapeModelKey) => {
                    const usingFonts = this.shapeModelUsingFonts.get(shapeModelKey);
                    if (!usingFonts?.has(latestDownloadedFont)) {
                        return;
                    }
                    callbacks.forEach(callback => callback(latestDownloadedFont));
                });
            },
        );

        this.disposeGlobalUsingFontsChangeReaction = reaction(
            () => this.globalUsingFontsCache.size,
            () => {
                [...this.globalUsingFontsCache].forEach((font) => {
                    this.triggerFontDownload(font);
                });
            },
        );
    }

    private iterateOnModelChildren(model: IBaseModel, usingFonts: Set<string>): void {
        if (!model) {
            return;
        }
        if (isShapeModel(model)) {
            model.paragraphList?.forEach((paragraph) => {
                paragraph?.textRuns?.forEach((textRun) => {
                    if (textRun.styles?.fonts) {
                        if (textRun.styles.fonts.latin) {
                            usingFonts.add(textRun.styles.fonts.latin);
                        } else if (textRun.styles.fonts.ea) {
                            usingFonts.add(textRun.styles.fonts.ea);
                        }
                    }
                });
            });
        } else if (isTableModel(model)) {
            const children = model.tableItems?.reduce((acc, cur) => acc.concat(cur), []);
            children?.forEach(((childModel) => {
                this.iterateOnModelChildren(childModel, usingFonts);
            }));
        } else if (isGroupModel(model)) {
            model.children.forEach((childModel) => {
                this.iterateOnModelChildren(childModel, usingFonts);
            });
        }
    }

    /**
     * slide 针对腾讯体用户反馈比较多。在复制粘贴/导入等场景经常出现腾讯体的别名不触发云字体加载，给用户造成困惑。此方法用于定义
     * 各类字体的别名方便云字体库触发下载
     */
    private fallbackToFontNameAlias(fontFamily: string): string {
        if (['TencentSans W3', '腾讯体', '腾讯体W3'].includes(fontFamily)) {
            // eslint-disable-next-line @tencent/i18next/no-literal-string
            return '腾讯体 W3';
        }
        if (['TencentSans W7', '腾讯体W7'].includes(fontFamily)) {
            // eslint-disable-next-line @tencent/i18next/no-literal-string
            return '腾讯体 W7';
        }
        return fontFamily;
    }
}
